import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { IoIosArrowDropdownCircle } from 'react-icons/io';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Tooltip } from 'antd';
import ResizeHandle from 'components/common/ResizeHandle';
import RightPanelShell from 'components/RightPanel/RightPanelShell';
import {
  FARMS_LOAD_ERROR_MESSAGE,
  INPUTS_LOAD_ERROR_MESSAGE,
  UNITS_LOAD_ERROR_MESSAGE,
  WORKSPACE_LOAD_FATAL_ERROR_MESSAGE,
  ZONES_LOAD_ERROR_MESSAGE,
} from 'constants/messageConstants';
import { ORGANISATIONS_PAGE_PATH } from 'constants/routeConstants';
import {
  FARMS_PAGE_TITLE,
  SIDEBAR_COLLAPSE_LABEL,
  SIDEBAR_COLLAPSED_STORAGE_KEY,
  SIDEBAR_INITIAL_WIDTH,
  SIDEBAR_MAX_WIDTH,
  SIDEBAR_MIN_WIDTH,
  SIDEBAR_WIDTH_STORAGE_KEY,
} from 'constants/uiConstants';
import { useResizableSidebar } from 'hooks/common/useResizableSidebar';
import { useFetchInputsByOrgId } from 'hooks/inputs/useFetchInputsByOrgId';
import { useFetchFarmsByOrgId } from 'hooks/useFetchFarmsByOrgId';
import { useFetchGUnits } from 'hooks/useFetchGUnits';
import useFetchOrganisations from 'hooks/useFetchOrganisations';
import useFetchUserProfile from 'hooks/useFetchUserProfile';
import { useFetchZonesByOrgId } from 'hooks/useFetchZonesByOrgId';
import useNetworkStatus from 'hooks/useNetworkStatus';
import { useOrganisationCollectionMutation } from 'hooks/useOrganisationCollectionMutation';
import { InputType } from 'interfaces/IInput';
import { toast } from 'utils/toast';

import {
  clearFarmSelection,
  setSidebarCollapsed,
} from '../../redux/slices/FarmSlice';
import { clearInputSelection } from '../../redux/slices/InputSlice';
import { RootState } from '../../redux/Store';

import useFarmsNavSync from './hooks/useFarmsNavSync';
import OrganisationTreeView from './Sidebar/OrganisationTreeView';
import OrganisationWorkspace from './Workspace/OrganisationWorkspace';

const FarmsPage: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  useFarmsNavSync();
  // Dynamically tightened by RightPanelShell whenever the right panel + edge
  // strip would be pushed below their minimum combined width.
  const sidebarMaxRef = useRef(SIDEBAR_MAX_WIDTH);
  const { containerRef, sidebarRef, width, widthRef, setWidth, onMouseDown } =
    useResizableSidebar({
      min: SIDEBAR_MIN_WIDTH,
      max: SIDEBAR_MAX_WIDTH,
      maxRef: sidebarMaxRef,
      initial: SIDEBAR_INITIAL_WIDTH,
    });

  const { organisationId: activeOrganisationId, isSwitchingOrganisation } =
    useSelector((state: RootState) => state.activeOrganisationReducer);

  const sidebarCollapsed = useSelector(
    (state: RootState) => state.farmReducer.sidebarCollapsed
  );
  const sidebarWidthResetCounter = useSelector(
    (state: RootState) => state.farmReducer.sidebarWidthResetCounter
  );

  // ── Data hooks (cache-shared with sidebar — no duplicate requests) ──
  const { data: profileData, isError: profileError } = useFetchUserProfile();
  const { data: orgsData, isError: orgsError } = useFetchOrganisations();
  const { data: farmsData, isError: farmsError } =
    useFetchFarmsByOrgId(activeOrganisationId);
  const { data: inputsData, isError: inputsError } = useFetchInputsByOrgId(
    activeOrganisationId,
    InputType.PlanActual
  );
  const { data: zonesData, isError: zonesError } =
    useFetchZonesByOrgId(activeOrganisationId);

  // GOrganisation mutation → GUnits preload (lifted from OrganisationWorkspace)
  const activeGOrganisation = useSelector(
    (state: RootState) => state.globalCatalogueReducer.activeGOrganisation
  );
  const { mutate: fetchGOrganisation } = useOrganisationCollectionMutation();

  useEffect(() => {
    if (!activeGOrganisation) {
      fetchGOrganisation();
    }
  }, [activeGOrganisation, fetchGOrganisation]);

  const gOrgId = activeGOrganisation || null;
  const { data: gUnitsData, isError: gUnitsError } = useFetchGUnits(gOrgId);

  // ── Offline handling during initial load ──
  // When the network drops mid-load, React Query pauses in-flight requests
  // rather than erroring them, so completionFlags may never all become true.
  // We resolve this by treating any still-pending non-fatal flag as complete
  // (empty data) so the workspace can render with what was already received.
  //
  // Fatal case: if profile or orgs data never arrived before the disconnect,
  // the workspace has no user context — treat identically to an API fatal error.
  const isOnline = useNetworkStatus();
  const isOffline = !isOnline;
  // isOfflineFatal: offline AND at least one of the two mandatory datasets
  // (profile, orgs) is still absent. Without these the workspace cannot render.
  const isOfflineFatal = isOffline && (!profileData || !orgsData);
  // forceComplete: offline but we have enough context to show the workspace —
  // treat remaining unresolved non-fatal flags as resolved (empty data).
  const forceComplete = isOffline && !isOfflineFatal;

  // ── Progress tracking — one flag per API step ──
  // During org switch, farms / zones / inputs are refetched for the new org;
  // force their flags to false so the progress bar reappears.
  // Non-fatal errors (API 2-5) are treated as resolved so the bar can complete.
  // forceComplete also resolves non-fatal flags when offline mid-load.
  const completionFlags = useMemo(
    () =>
      isSwitchingOrganisation
        ? [
            !!profileData,
            !!orgsData,
            forceComplete,
            forceComplete,
            forceComplete,
            !!gUnitsData || gUnitsError || forceComplete,
          ]
        : [
            !!profileData,
            !!orgsData,
            !!farmsData || farmsError || forceComplete,
            !!zonesData || zonesError || forceComplete,
            !!inputsData || inputsError || forceComplete,
            !!gUnitsData || gUnitsError || forceComplete,
          ],
    [
      isSwitchingOrganisation,
      profileData,
      orgsData,
      farmsData,
      farmsError,
      inputsData,
      inputsError,
      zonesData,
      zonesError,
      gUnitsData,
      gUnitsError,
      forceComplete,
    ]
  );
  const isAllLoaded = completionFlags.every(Boolean);

  // Fatal errors — dismiss progress immediately and show error toast.
  // Covers both API failures on profile/orgs and offline-with-no-cached-data.
  const isFatalError = profileError || orgsError || isOfflineFatal;

  // Wait for tree to finish rendering before hiding progress.
  // Initialised to isAllLoaded so cached data skips progress entirely.
  const [progressDone, setProgressDone] = useState(isAllLoaded);

  useEffect(() => {
    if (isFatalError && !progressDone) {
      toast.error(WORKSPACE_LOAD_FATAL_ERROR_MESSAGE);
      setProgressDone(true);
    }
  }, [isFatalError, progressDone]);

  useEffect(() => {
    if (isAllLoaded && !progressDone && !isFatalError) {
      // Data ready → give the last curve pulse time to animate
      // (approach 800 ms + hold 300 ms + buffer)
      const timer = setTimeout(() => setProgressDone(true), 1200);
      return () => clearTimeout(timer);
    }
    if (!isAllLoaded && !isFatalError) {
      setProgressDone(false);
    }
  }, [isAllLoaded, progressDone, isFatalError]);

  // Non-fatal errors — show a toast per failed API (fires once per error).
  // The sidebar tree treats farms and zones errors as fatal (blocks tree render),
  // but inputs failure is non-fatal there too: Farm/Paddock nodes still render
  // without plan data. See FarmTreeView isError for the matching exclusion.
  useEffect(() => {
    if (farmsError) toast.error(FARMS_LOAD_ERROR_MESSAGE);
  }, [farmsError]);

  useEffect(() => {
    if (zonesError) toast.error(ZONES_LOAD_ERROR_MESSAGE);
  }, [zonesError]);

  useEffect(() => {
    if (inputsError) toast.error(INPUTS_LOAD_ERROR_MESSAGE);
  }, [inputsError]);

  useEffect(() => {
    if (gUnitsError) toast.error(UNITS_LOAD_ERROR_MESSAGE);
  }, [gUnitsError]);

  const dataSizes = useMemo(
    () =>
      isSwitchingOrganisation
        ? [
            profileData ? JSON.stringify(profileData).length : 0,
            orgsData ? JSON.stringify(orgsData).length : 0,
            0,
            0,
            0,
            gUnitsData ? JSON.stringify(gUnitsData).length : 0,
          ]
        : [
            profileData ? JSON.stringify(profileData).length : 0,
            orgsData ? JSON.stringify(orgsData).length : 0,
            farmsData ? JSON.stringify(farmsData).length : 0,
            zonesData ? JSON.stringify(zonesData).length : 0,
            inputsData ? JSON.stringify(inputsData).length : 0,
            gUnitsData ? JSON.stringify(gUnitsData).length : 0,
          ],
    [
      isSwitchingOrganisation,
      profileData,
      orgsData,
      farmsData,
      inputsData,
      zonesData,
      gUnitsData,
    ]
  );

  useEffect(() => {
    if (!activeOrganisationId && !isSwitchingOrganisation) {
      navigate(ORGANISATIONS_PAGE_PATH);
    }
  }, [activeOrganisationId, isSwitchingOrganisation, navigate]);

  useEffect(() => {
    const saved = localStorage.getItem(SIDEBAR_WIDTH_STORAGE_KEY);
    if (saved) {
      const value = Number(saved);
      // Guard against stale px values stored before the rem migration:
      // valid rem values fall within [SIDEBAR_MIN_WIDTH, SIDEBAR_MAX_WIDTH].
      // A legacy px value (e.g. 288) would exceed SIDEBAR_MAX_WIDTH (30) and
      // is silently discarded, falling back to the initial rem default.
      if (value >= SIDEBAR_MIN_WIDTH && value <= SIDEBAR_MAX_WIDTH) {
        widthRef.current = value;
        setWidth(value);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(SIDEBAR_WIDTH_STORAGE_KEY, width.toString());
  }, [width]);

  useEffect(() => {
    const saved = localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY);
    if (saved === 'true') {
      dispatch(setSidebarCollapsed(true));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(
      SIDEBAR_COLLAPSED_STORAGE_KEY,
      String(sidebarCollapsed)
    );
  }, [sidebarCollapsed]);

  useEffect(() => {
    if (sidebarWidthResetCounter === 0) return;
    widthRef.current = SIDEBAR_INITIAL_WIDTH;
    setWidth(SIDEBAR_INITIAL_WIDTH);
  }, [sidebarWidthResetCounter]);

  useEffect(() => {
    return () => {
      dispatch(clearInputSelection());
      dispatch(clearFarmSelection());
    };
  }, [dispatch]);

  return (
    <>
      <Helmet title={FARMS_PAGE_TITLE} />

      <div
        ref={containerRef}
        className="relative flex h-screen overflow-hidden"
      >
        {!sidebarCollapsed && (
          <div
            ref={sidebarRef}
            style={{ '--sidebar-width': `${width}rem` } as React.CSSProperties}
            className="farms-sidebar min-w-0 overflow-hidden border-r"
          >
            <OrganisationTreeView forceReady={progressDone} />
          </div>
        )}

        {!sidebarCollapsed && (
          <div
            onMouseDown={onMouseDown}
            onDoubleClick={() => setWidth(SIDEBAR_INITIAL_WIDTH)}
            role="separator"
            aria-orientation="vertical"
            tabIndex={0}
            className="group relative z-20 flex w-1 cursor-col-resize items-center justify-center bg-transparent transition-all hover:w-1.5 hover:bg-gray-argent"
          >
            <ResizeHandle />
            {/* Collapse button — top third, visible on hover only. The icon
                  grows when the surrounding resize-handle line is hovered
                  (`group-hover`, from the outer div above), not on its own
                  hover — the button is small enough that requiring a hover
                  directly on it defeats the point of making it easier to hit. */}
            <div className="pointer-events-none absolute inset-x-0 top-0 flex h-1/3 items-center justify-center opacity-0 transition-opacity group-hover:pointer-events-auto group-hover:opacity-100">
              <Tooltip title={SIDEBAR_COLLAPSE_LABEL} placement="right">
                <button
                  type="button"
                  aria-label={SIDEBAR_COLLAPSE_LABEL}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={() => dispatch(setSidebarCollapsed(true))}
                  className="rotate-90 text-gray-dark transition-opacity hover:opacity-75"
                >
                  <IoIosArrowDropdownCircle
                    size={18}
                    className="transition-transform duration-150 group-hover:scale-[2]"
                  />
                </button>
              </Tooltip>
            </div>
          </div>
        )}

        <OrganisationWorkspace
          completionFlags={progressDone ? undefined : completionFlags}
          dataSizes={progressDone ? undefined : dataSizes}
        />

        <RightPanelShell
          sidebarWidth={sidebarCollapsed ? 0 : width}
          sidebarRef={sidebarRef}
          sidebarWidthRef={sidebarCollapsed ? undefined : widthRef}
          containerRef={containerRef}
          sidebarMaxRef={sidebarCollapsed ? undefined : sidebarMaxRef}
          setSidebarWidth={sidebarCollapsed ? undefined : setWidth}
        />
      </div>
    </>
  );
};

export default FarmsPage;
